import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createSubscriptionCheckoutSession } from '@/api/checkout.api';
import { WEAVE_PAYWALL_EVENT_NAMES } from '@/consts/analytics.consts';
import { FF_ANNUAL_DISCOUNT_30 } from '@/consts/featureFlags';
import { getPlanPrices } from '@/consts/pricing.consts';
import { WEAVY_PAYWALL_ENTRYPOINTS } from '@/consts/weavyPaywallEntrypoints.consts';
import { useHideTourWhile } from '@/components/ProductTours/TourContext';
import { AuthContext } from '@/contexts/AuthContext';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { log } from '@/logger/logger';
import { CreditsContext } from '@/services/CreditsContext';
import { useWeavyPaywallStore } from '@/state/weavyPaywall.state';
import { BillingCycle, SubscriptionType } from '@/types/shared';
import { WeavyPaywallModal, type WeavyPaywallModalActionParams } from './WeavyPaywallModal';

const logger = log.getLogger('WeavyPaywallController');

/** Resolves and renders the active paywall entirely from the entrypoints registry. */
export function WeavyPaywallController() {
  const activeEntrypoint = useWeavyPaywallStore((state) => state.activeEntrypoint);
  const closePaywall = useWeavyPaywallStore((state) => state.closePaywall);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const { currentUser } = useContext(AuthContext);
  const { openUpgradeModal, openUpgradeSubscriptionModal } = useContext(CreditsContext);
  const { track } = useAnalytics();
  const isAnnualDiscount30Enabled = useFeatureFlag(FF_ANNUAL_DISCOUNT_30).value === true;
  const entrypointConfig = activeEntrypoint ? WEAVY_PAYWALL_ENTRYPOINTS[activeEntrypoint] : null;
  const workspaceId = currentUser?.activeWorkspace.workspaceId;
  const lastViewedEntrypointRef = useRef<string | null>(null);

  // pause active tours while the paywall is open
  useHideTourWhile(entrypointConfig !== null);

  const trackPaywallEvent = useCallback(
    (event: string, properties: Record<string, unknown> = {}) => {
      if (!activeEntrypoint || !entrypointConfig) return;

      track(
        event,
        {
          entrypoint: activeEntrypoint,
          current_plan: entrypointConfig.planTier,
          target_plan: entrypointConfig.targetPlanTier,
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
          ...properties,
        },
        TrackTypeEnum.BI,
      );
    },
    [activeEntrypoint, entrypointConfig, track, workspaceId],
  );

  useEffect(() => {
    if (!activeEntrypoint) {
      lastViewedEntrypointRef.current = null;
      return;
    }
    if (lastViewedEntrypointRef.current === activeEntrypoint) return;

    lastViewedEntrypointRef.current = activeEntrypoint;
    trackPaywallEvent(WEAVE_PAYWALL_EVENT_NAMES.VIEWED);
  }, [activeEntrypoint, trackPaywallEvent]);

  const startCheckout = useCallback(
    async (billingCycle: BillingCycle) => {
      if (!activeEntrypoint || !entrypointConfig || !workspaceId || isCheckoutLoading) return;

      setIsCheckoutLoading(true);

      // set redirect url on successful payment - same as pricing page behavior
      const callbackUrl = new URL(window.location.href);
      callbackUrl.searchParams.set('payment', 'upgrade');
      callbackUrl.searchParams.set('plan', entrypointConfig.targetPlanTier);
      callbackUrl.searchParams.set('entrypoint', activeEntrypoint);

      try {
        const checkoutSessionUrl = await createSubscriptionCheckoutSession({
          workspaceId,
          subscriptionType: entrypointConfig.targetPlanTier,
          billingCycle,
          callbackUrl: callbackUrl.toString(),
        });

        if (checkoutSessionUrl) {
          trackPaywallEvent(WEAVE_PAYWALL_EVENT_NAMES.CHECKOUT_RESULT, {
            billing_cycle: billingCycle,
            result: 'success',
          });
          window.location.href = checkoutSessionUrl;
        } else {
          trackPaywallEvent(WEAVE_PAYWALL_EVENT_NAMES.CHECKOUT_RESULT, {
            billing_cycle: billingCycle,
            result: 'failure',
            failure_reason: 'missing_checkout_url',
          });
        }
      } catch (error) {
        trackPaywallEvent(WEAVE_PAYWALL_EVENT_NAMES.CHECKOUT_RESULT, {
          billing_cycle: billingCycle,
          result: 'failure',
          failure_reason: 'request_failed',
        });
        logger.error('Failed getting paywall checkout session', error);
      } finally {
        setIsCheckoutLoading(false);
      }
    },
    [activeEntrypoint, entrypointConfig, isCheckoutLoading, trackPaywallEvent, workspaceId],
  );

  const handleAction = useCallback(
    // billingCycle is enforced here because checkout_plan requires it
    // better to always require it than handle missing billingCycle value and error on checkout
    ({ action, billingCycle }: WeavyPaywallModalActionParams) => {
      if (action.type === 'checkout_plan') {
        trackPaywallEvent(WEAVE_PAYWALL_EVENT_NAMES.CTA_CLICKED, {
          action: action.type,
          billing_cycle: billingCycle,
        });
        // As on the pricing page, only Free goes through Stripe checkout; the server refuses a checkout session
        // for a workspace that already has a subscription, which upgrades it in place instead.
        const subscription = currentUser?.activeWorkspace.subscription;
        if (entrypointConfig && subscription?.type && subscription.type !== SubscriptionType.Free) {
          const prices = getPlanPrices(entrypointConfig.targetPlanTier, isAnnualDiscount30Enabled);
          closePaywall();
          openUpgradeSubscriptionModal({
            name: entrypointConfig.targetPlanTier,
            billingCycle,
            price: billingCycle === BillingCycle.Monthly ? prices.monthlyPrice : prices.annualPrice,
            annualTotal: prices.annualTotal,
            numberOfSeats: subscription.seatsLimit,
            entrypoint: activeEntrypoint ?? undefined,
          });
          return;
        }
        void startCheckout(billingCycle);
        return;
      } else if (action.type === 'open_plan_comparison') {
        trackPaywallEvent(WEAVE_PAYWALL_EVENT_NAMES.CTA_CLICKED, {
          action: action.type,
        });
        openUpgradeModal({
          onOpen: closePaywall,
          analyticsContext: activeEntrypoint ? { entrypoint: activeEntrypoint } : undefined,
        });
        return;
      } else if (action.type === 'close') {
        trackPaywallEvent(WEAVE_PAYWALL_EVENT_NAMES.DISMISSED);
        closePaywall();
        return;
      }
    },
    [
      activeEntrypoint,
      closePaywall,
      currentUser,
      entrypointConfig,
      isAnnualDiscount30Enabled,
      openUpgradeModal,
      openUpgradeSubscriptionModal,
      startCheckout,
      trackPaywallEvent,
    ],
  );

  const handleBillingCycleChange = useCallback(
    (billingCycle: BillingCycle) => {
      trackPaywallEvent(WEAVE_PAYWALL_EVENT_NAMES.BILLING_CYCLE_CHANGED, {
        billing_cycle: billingCycle,
      });
    },
    [trackPaywallEvent],
  );

  if (!entrypointConfig) return null;

  return (
    <WeavyPaywallModal
      entrypointConfig={entrypointConfig}
      isCheckoutLoading={isCheckoutLoading}
      onAction={handleAction}
      onBillingCycleChange={handleBillingCycleChange}
    />
  );
}
